import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ListSectionTemplate from "../../common/ListSectionTemplate";
import OrgDetails from "./OrgDetails";
import { clearDemoSessions, createOrg, listAllOrgs, listDemoRequests, listDemoSessions, setDemoRequestStatus } from "../../../api/studioAPI";
import { colors, surfaces, buttons, form } from "../../../styles/index";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : "-");
const row = { display: "flex", gap: 12, padding: "10px 16px", borderBottom: `1px solid ${colors.border}`, flexWrap: "wrap", alignItems: "center" };
const muted = { color: colors.textMuted, fontSize: "0.85rem" };
const tab = (on) => ({ ...(on ? buttons.primary : buttons.secondary), minHeight: 36, padding: "6px 14px" });

// The platform admin's view (role "admin" only - the backend checks too): every customer org - open one to fix its members,
// or set up a new customer with an owner invitation - the "book a demo" leads to follow up, and who is trying the demo.
export default function PlatformPanel() {
  const [view, setView] = useState("orgs");            // "orgs" | "requests" | "demos" | an org id
  const [orgs, setOrgs] = useState(null);
  const [demos, setDemos] = useState(null);
  const [requests, setRequests] = useState(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("");
  const [lastLink, setLastLink] = useState("");

  const load = useCallback(() => {
    listAllOrgs().then((d) => setOrgs(d.items)).catch((e) => toast.error(e.message));
    listDemoSessions().then(setDemos).catch((e) => toast.error(e.message));
    listDemoRequests().then(setRequests).catch((e) => toast.error(e.message));
  }, []);

  const followUp = async (r, status) => {
    try {
      await setDemoRequestStatus(r.email, r.request_id, status);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };
  useEffect(() => { load(); }, [load]);

  const addOrg = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const out = await createOrg(name.trim(), owner.trim());
      toast.success(`${out.org.org_name} created - invitation sent to ${out.invited}`);
      setName(""); setOwner(""); setLastLink(out.link || "");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const clearDemos = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete every demo org now, with its plans and games - even ones still in use?")) return;
    setBusy(true);
    try {
      const out = await clearDemoSessions();
      toast.success(`Cleared ${out.orgs_cleared} demo org(s)`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!["orgs", "requests", "demos"].includes(view)) {
    return <OrgDetails orgId={view} onBack={() => { setView("orgs"); load(); }} />;
  }

  const switcher = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button type="button" style={tab(view === "orgs")} onClick={() => setView("orgs")}>Customers</button>
      <button type="button" style={tab(view === "requests")} onClick={() => setView("requests")}>
        Demo requests{requests ? ` (${requests.new} new)` : ""}
      </button>
      <button type="button" style={tab(view === "demos")} onClick={() => setView("demos")}>
        Demo sessions{demos ? ` (${demos.active} active)` : ""}
      </button>
    </div>
  );

  if (view === "requests") {
    return (
      <ListSectionTemplate title="Platform" loading={requests === null} filters={switcher}>
        <div style={{ ...surfaces.card, textAlign: "left", padding: 0 }}>
          {(requests?.items || []).map((r) => (
            <div key={`${r.email}/${r.request_id}`} style={row}>
              <div style={{ flex: "1 1 260px" }}>
                <strong>{r.company}</strong> - {r.name} &lt;<a href={`mailto:${r.email}`}>{r.email}</a>&gt;
                <div style={muted}>{new Date(r.created_at).toLocaleString()}{r.team_size ? ` · ${r.team_size} people` : ""}</div>
                {r.notes && <div style={{ fontSize: "0.9rem", marginTop: 4 }}>{r.notes}</div>}
              </div>
              <select aria-label={`Status of ${r.company}`} value={r.status} onChange={(e) => followUp(r, e.target.value)}
                style={{ ...form.input, width: "auto", minHeight: 36, padding: "6px 10px" }}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          ))}
          {requests && requests.items.length === 0 && <p style={{ padding: 16, margin: 0, color: colors.textMuted }}>No demo requests yet.</p>}
        </div>
      </ListSectionTemplate>
    );
  }

  if (view === "demos") {
    return (
      <ListSectionTemplate title="Platform" loading={demos === null} filters={switcher}
        actions={<button type="button" style={buttons.secondary} onClick={clearDemos} disabled={busy}>Clear demo orgs</button>}>
        <div style={{ ...surfaces.card, textAlign: "left", padding: 0 }}>
          {(demos?.items || []).map((d) => (
            <div key={d.org_id} style={row}>
              <strong style={{ flex: "1 1 160px" }}>{d.org_name}</strong>
              <span style={{ ...muted, color: d.active ? colors.textDark : colors.textMuted, fontWeight: d.active ? 600 : 400 }}>
                {d.active ? "active" : "finished"}
              </span>
              <span style={muted}>started {d.started_at ? new Date(d.started_at).toLocaleString() : "-"}</span>
              <span style={muted}>{d.plans} plan(s)</span>
              <span style={muted}>from {d.source || "-"}</span>
            </div>
          ))}
          {demos && demos.items.length === 0 && <p style={{ padding: 16, margin: 0, color: colors.textMuted }}>Nobody is trying the demo.</p>}
        </div>
      </ListSectionTemplate>
    );
  }

  return (
    <ListSectionTemplate title="Platform" loading={orgs === null} filters={switcher}>
      <form onSubmit={addOrg} style={{ ...surfaces.card, textAlign: "left", marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ flex: "2 1 200px" }}>
          <span style={form.label}>New customer organization</span>
          <input style={form.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Foods" disabled={busy} />
        </label>
        <label style={{ flex: "2 1 220px" }}>
          <span style={form.label}>Its owner&rsquo;s email</span>
          <input type="email" style={form.input} value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="owner@acme.com" disabled={busy} />
        </label>
        <button type="submit" style={buttons.primary} disabled={busy || !name.trim() || !owner.trim()}>Create and invite</button>
        {lastLink && (
          <p style={{ flexBasis: "100%", margin: 0, fontSize: "0.85rem", wordBreak: "break-all" }}>
            Email isn&rsquo;t set up on this server - send the owner this link yourself: <code>{lastLink}</code>
          </p>
        )}
      </form>
      <div style={{ ...surfaces.card, textAlign: "left", padding: 0 }}>
        {(orgs || []).map((o) => (
          <button key={o.org_id} type="button" onClick={() => setView(o.org_id)}
            style={{ ...row, width: "100%", background: "none", border: "none", borderBottom: `1px solid ${colors.border}`, cursor: "pointer", textAlign: "left", font: "inherit" }}>
            <strong style={{ flex: "1 1 200px" }}>{o.org_name}</strong>
            <span style={muted}>{o.members} member(s) · {o.plans} plan(s)</span>
            <span style={muted}>{o.owners.join(", ") || "no owner yet"}</span>
            <span style={muted}>created {day(o.created_at)} · last sign-in {day(o.last_login)}</span>
          </button>
        ))}
        {orgs && orgs.length === 0 && <p style={{ padding: 16, margin: 0, color: colors.textMuted }}>No organizations yet.</p>}
      </div>
    </ListSectionTemplate>
  );
}
